import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Eye, MousePointerClick, Target, Clock, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#4f46e5', '#84cc16', '#06b6d4', '#f59e0b', '#ef4444'];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);

  const storeId = localStorage.getItem('store_id');

  useEffect(() => {
    if (!storeId) {
      navigate('/');
      return;
    }

    fetchAnalytics();
  }, [storeId, period, navigate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard?days=${period}`, {
        headers: {
          'X-Store-Id': storeId
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center' data-testid='analytics-loading'>
        <div className='text-center'>
          <BarChart3 className='w-12 h-12 animate-pulse text-primary mx-auto mb-4' />
          <p className='text-zinc-600'>Cargando analytics...</p>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const topProducts = analytics?.top_products || [];
  const timeline = analytics?.timeline || [];

  const productDistribution = topProducts.map((product, index) => ({
    name: product.product_name,
    value: product.views,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className='min-h-screen bg-[#f8fafc]' data-testid='analytics-page'>
      <header className='bg-white border-b border-zinc-200 sticky top-0 z-10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
          <Button
            onClick={() => navigate('/dashboard')}
            variant='ghost'
            className='gap-2 mb-4'
            data-testid='back-to-dashboard-button'
          >
            <ArrowLeft className='w-4 h-4' />
            Volver al Dashboard
          </Button>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-3xl font-bold text-zinc-900'>Analytics de Engagement</h1>
              <p className='text-zinc-600 mt-2'>Métricas de interacción con spins 360° y hotspots</p>
            </div>
            <div className='flex gap-2'>
              <Button
                variant={period === 7 ? 'default' : 'outline'}
                size='sm'
                onClick={() => setPeriod(7)}
                data-testid='period-7-days'
              >
                7 días
              </Button>
              <Button
                variant={period === 30 ? 'default' : 'outline'}
                size='sm'
                onClick={() => setPeriod(30)}
                data-testid='period-30-days'
              >
                30 días
              </Button>
              <Button
                variant={period === 90 ? 'default' : 'outline'}
                size='sm'
                onClick={() => setPeriod(90)}
                data-testid='period-90-days'
              >
                90 días
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
          <Card data-testid='stat-total-views'>
            <CardHeader className='pb-3'>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                  <Eye className='w-5 h-5 text-blue-600' />
                </div>
                <CardTitle className='text-sm font-medium text-zinc-500'>Total Vistas</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-zinc-900'>{summary.total_views || 0}</p>
              <p className='text-xs text-zinc-500 mt-1'>Visualizaciones de spin 360°</p>
            </CardContent>
          </Card>

          <Card data-testid='stat-interactions'>
            <CardHeader className='pb-3'>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center'>
                  <MousePointerClick className='w-5 h-5 text-primary' />
                </div>
                <CardTitle className='text-sm font-medium text-zinc-500'>Interacciones</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-zinc-900'>{summary.total_interactions || 0}</p>
              <p className='text-xs text-zinc-500 mt-1'>Rotaciones, zooms y clicks</p>
            </CardContent>
          </Card>

          <Card data-testid='stat-sessions'>
            <CardHeader className='pb-3'>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center'>
                  <Target className='w-5 h-5 text-accent' />
                </div>
                <CardTitle className='text-sm font-medium text-zinc-500'>Sesiones Únicas</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-zinc-900'>{summary.unique_sessions || 0}</p>
              <p className='text-xs text-zinc-500 mt-1'>Usuarios únicos</p>
            </CardContent>
          </Card>

          <Card data-testid='stat-engagement-rate'>
            <CardHeader className='pb-3'>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                  <TrendingUp className='w-5 h-5 text-green-600' />
                </div>
                <CardTitle className='text-sm font-medium text-zinc-500'>Tasa de Engagement</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-bold text-zinc-900'>{summary.engagement_rate || 0}%</p>
              <p className='text-xs text-zinc-500 mt-1'>Interacciones / Vistas</p>
            </CardContent>
          </Card>
        </div>

        <Card className='mb-8' data-testid='timeline-chart'>
          <CardHeader>
            <CardTitle>Tendencia de Engagement</CardTitle>
            <CardDescription>Vistas e interacciones en el tiempo</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                <XAxis dataKey='date' stroke='#6b7280' fontSize={12} />
                <YAxis stroke='#6b7280' fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line
                  type='monotone'
                  dataKey='views'
                  stroke='#4f46e5'
                  strokeWidth={2}
                  name='Vistas'
                  dot={{ fill: '#4f46e5' }}
                />
                <Line
                  type='monotone'
                  dataKey='interactions'
                  stroke='#84cc16'
                  strokeWidth={2}
                  name='Interacciones'
                  dot={{ fill: '#84cc16' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
          <Card data-testid='top-products-chart'>
            <CardHeader>
              <CardTitle>Top 5 Productos</CardTitle>
              <CardDescription>Productos con mayor engagement</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width='100%' height={300}>
                <BarChart data={topProducts}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                  <XAxis dataKey='product_name' stroke='#6b7280' fontSize={11} angle={-45} textAnchor='end' height={80} />
                  <YAxis stroke='#6b7280' fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey='views' fill='#4f46e5' name='Vistas' />
                  <Bar dataKey='interactions' fill='#84cc16' name='Interacciones' />
                  <Bar dataKey='hotspot_clicks' fill='#06b6d4' name='Clicks en Hotspots' />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card data-testid='product-distribution-chart'>
            <CardHeader>
              <CardTitle>Distribución de Vistas</CardTitle>
              <CardDescription>Porcentaje de vistas por producto</CardDescription>
            </CardHeader>
            <CardContent>
              {productDistribution.length > 0 ? (
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={productDistribution}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={({ name, percent }) => `${name.substring(0, 20)} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill='#8884d8'
                      dataKey='value'
                    >
                      {productDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className='h-[300px] flex items-center justify-center text-zinc-500'>
                  No hay datos suficientes
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className='mt-8' data-testid='products-table'>
          <CardHeader>
            <CardTitle>Detalle por Producto</CardTitle>
            <CardDescription>Métricas individuales de cada producto</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-zinc-200'>
                    <th className='text-left py-3 px-4 text-sm font-semibold text-zinc-700'>Producto</th>
                    <th className='text-right py-3 px-4 text-sm font-semibold text-zinc-700'>Vistas</th>
                    <th className='text-right py-3 px-4 text-sm font-semibold text-zinc-700'>Interacciones</th>
                    <th className='text-right py-3 px-4 text-sm font-semibold text-zinc-700'>Clicks en Hotspots</th>
                    <th className='text-right py-3 px-4 text-sm font-semibold text-zinc-700'>Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, index) => {
                    const engagementRate = product.views > 0
                      ? ((product.interactions + product.hotspot_clicks) / product.views * 100).toFixed(1)
                      : 0;
                    
                    return (
                      <tr
                        key={index}
                        className='border-b border-zinc-100 hover:bg-zinc-50 transition-colors'
                        data-testid={`product-row-${index}`}
                      >
                        <td className='py-3 px-4'>
                          <p className='font-medium text-zinc-900'>{product.product_name}</p>
                          <p className='text-xs text-zinc-500'>ID: {product.product_id}</p>
                        </td>
                        <td className='text-right py-3 px-4 text-zinc-900'>{product.views}</td>
                        <td className='text-right py-3 px-4 text-zinc-900'>{product.interactions}</td>
                        <td className='text-right py-3 px-4 text-zinc-900'>{product.hotspot_clicks}</td>
                        <td className='text-right py-3 px-4'>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            engagementRate > 80 ? 'bg-green-100 text-green-800' :
                            engagementRate > 50 ? 'bg-blue-100 text-blue-800' :
                            'bg-zinc-100 text-zinc-800'
                          }`}>
                            {engagementRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className='mt-8 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20' data-testid='insights-card'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <TrendingUp className='w-5 h-5 text-primary' />
              Insights y Recomendaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {summary.engagement_rate > 70 && (
                <div className='flex items-start gap-3'>
                  <div className='w-2 h-2 bg-green-500 rounded-full mt-2'></div>
                  <p className='text-sm text-zinc-700'>
                    <strong>Excelente engagement:</strong> Tu tasa de engagement del {summary.engagement_rate}% es muy buena. Los usuarios están interactuando activamente con tus productos 360°.
                  </p>
                </div>
              )}
              {summary.engagement_rate < 30 && summary.total_views > 10 && (
                <div className='flex items-start gap-3'>
                  <div className='w-2 h-2 bg-orange-500 rounded-full mt-2'></div>
                  <p className='text-sm text-zinc-700'>
                    <strong>Oportunidad de mejora:</strong> Tu tasa de engagement es del {summary.engagement_rate}%. Considera añadir más hotspots informativos o mejorar la calidad de las imágenes 360°.
                  </p>
                </div>
              )}
              {topProducts.length > 0 && topProducts[0].hotspot_clicks > 5 && (
                <div className='flex items-start gap-3'>
                  <div className='w-2 h-2 bg-blue-500 rounded-full mt-2'></div>
                  <p className='text-sm text-zinc-700'>
                    <strong>Hotspots efectivos:</strong> Los usuarios están haciendo clic en los hotspots. Esto indica que están interesados en conocer más detalles de los productos.
                  </p>
                </div>
              )}
              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-primary rounded-full mt-2'></div>
                <p className='text-sm text-zinc-700'>
                  <strong>Próximo paso:</strong> Analiza qué productos tienen mejor engagement y replica esas estrategias en otros productos de tu catálogo.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
